import type { FC } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useHover } from "@uidotdev/usehooks";
import { ArrowRight } from "lucide-react";
import { colors, radius, spacing } from "../design-tokens";
import { AnimatedSection } from "../primitives/animated-section";
import { BodySmall } from "../primitives/typography";

type HelpfulNextStep = {
  title: string;
  url: string;
};

type HelpfulNextStepsProps = {
  actions?: HelpfulNextStep[];
};

type ActionItemProps = {
  action: HelpfulNextStep;
};

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const Action = styled.a<{ isHovered: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.xl}px 0;
  border-top: 1px solid ${colors.border};
  border-bottom: none;
  background: ${colors.surface};
  text-decoration: none;
  transition: all 200ms ease-in-out;
  cursor: pointer;

  :last-child {
    border-bottom: 1px solid ${colors.border};
  }
`;

const ActionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md}px;
  flex: 1;
`;

const ActionTitle = styled(BodySmall)<{ isHovered: boolean }>`
  width: fit-content;
  margin: 0;
  text-decoration: none;
  transition: text-decoration 200ms ease-in-out;
  display: inline-block;
  
  ${props => props.isHovered && css`
    text-decoration: underline;
    text-decoration-thickness: 1.5px;
    text-underline-offset: 2px;
  `}
`;

const ActionIcon = styled.div<{ isHovered: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: ${spacing.xs}px;
  border-radius: ${radius.sm}px;
  background: transparent;
  color: ${colors.iconAccent};
  transition: background 200ms ease-in-out;
  
  ${props => props.isHovered && css`
    background: ${colors.surface};
  `}
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const ActionItem: FC<ActionItemProps> = ({ action }) => {
  const [hoverRef, isHovered] = useHover<HTMLAnchorElement>();

  return (
    <Action
      ref={hoverRef}
      isHovered={isHovered}
      href={action.url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <ActionContent>
        <ActionTitle isHovered={isHovered}>{action.title}</ActionTitle>
      </ActionContent>
      <ActionIcon isHovered={isHovered}>
        <ArrowRight />
      </ActionIcon>
    </Action>
  );
};

export const HelpfulNextSteps: FC<HelpfulNextStepsProps> = ({ actions }) => {
  // filter out actions that don't have a complete URL
  const filteredActions = actions?.filter(action => action.url.includes("https://"));

  return (
    <AnimatedSection
      heading="Helpful next steps"
      gap="2xl"
      animationType="none"
      isVisible={(filteredActions?.length ?? 0) > 0}
    >
      <ActionsContainer>
        {filteredActions?.map((action, index) => (
          <ActionItem
            key={`${action.url}-${index}`}
            action={action}
          />
        ))}
      </ActionsContainer>
    </AnimatedSection>
  );
};
